import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { BrandLogo, SignalMark } from "../components/Logo";
import { track } from "../lib/track";
import {
  Mic, Brain, CalendarDays, Sparkles, ListChecks, Radar, ArrowRight, Check,
  ShieldCheck, Lock, FileSearch, Network, Mail, Search,
  Quote, Waypoints, Gauge, BookOpen, Users, LogIn,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NAVY = "#0B1F3A";
export const BLUE = "#1F6FEB";
export const TEAL = "#20C997";

export const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.2, 0.6, 0.3, 1] },
};

/* ================= Layout ================= */
export function MarketingLayout({ children }) {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { track("pageview", pathname); }, [pathname]);
  return (
    <div className="bg-white text-[#0B1F3A] min-h-screen" data-testid="landing-page" style={{ fontFamily: "'Inter', sans-serif" }}>
      <MarketingNav />
      {children}
      <Footer />
    </div>
  );
}

function MarketingNav() {
  const { pathname } = useLocation();
  const links = [
    ["How it works", "/how-it-works"],
    ["Platform", "/platform"],
    ["Compare", "/compare"],
    ["Security", "/security"],
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#0B1F3A]/8">
      <div className="mx-auto max-w-6xl px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="landing-brand"><BrandLogo /></Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          {links.map(([label, to]) => (
            <Link key={to} to={to} className={`transition-colors ${pathname === to ? "text-[#1F6FEB] font-medium" : "text-[#0B1F3A]/70 hover:text-[#0B1F3A]"}`}>{label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login-btn" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0B1F3A]/70 hover:text-[#0B1F3A] transition-colors">
            <LogIn className="h-4 w-4" strokeWidth={1.5} /> Login
          </Link>
          <Link to="/book-demo" data-testid="landing-nav-demo-btn" className="rounded-full bg-[#1F6FEB] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#1a5fd0] transition-colors">
            Book a demo
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-white border-t border-[#0B1F3A]/8">
      <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div>
          <BrandLogo tagline="Intelligence Platform" />
          <p className="text-xs text-[#0B1F3A]/45 mt-4 max-w-sm leading-relaxed">
            Transform every scientific conversation into strategic intelligence.
            Prepare · Meet · Capture · Follow up · Learn · Intelligence.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-6 text-sm text-[#0B1F3A]/60">
          <Link to="/how-it-works" className="hover:text-[#0B1F3A] transition-colors">How it works</Link>
          <Link to="/platform" className="hover:text-[#0B1F3A] transition-colors">Platform</Link>
          <Link to="/compare" className="hover:text-[#0B1F3A] transition-colors">Compare</Link>
          <Link to="/security" className="hover:text-[#0B1F3A] transition-colors">Security</Link>
          <Link to="/book-demo" className="hover:text-[#0B1F3A] transition-colors">Book a demo</Link>
          <Link to="/login" data-testid="footer-login-btn" className="rounded-full border border-[#0B1F3A]/15 px-5 py-2.5 font-medium hover:bg-[#F8FAFC] transition-colors inline-flex items-center gap-1.5">
            <LogIn className="h-4 w-4" strokeWidth={1.5} /> Login
          </Link>
        </div>
      </div>
      <div className="border-t border-[#0B1F3A]/8 py-6 text-center text-xs text-[#0B1F3A]/40">
        <p>© 2026 MA Signals™ · India pilot · Not for promotional use</p>
        <p className="mt-1.5 tracking-wide" data-testid="marksman-branding">
          A <span className="font-medium text-[#0B1F3A]/55">Marksman Healthcare</span> initiative
        </p>
      </div>
    </footer>
  );
}

export function SectionHead({ overline, title, sub, dark }) {
  return (
    <motion.div {...fadeUp} className="max-w-2xl mb-14">
      <p className="text-xs font-semibold tracking-[0.22em] uppercase mb-3" style={{ color: BLUE }}>{overline}</p>
      <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight leading-tight ${dark ? "text-white" : ""}`}>{title}</h2>
      {sub && <p className={`mt-4 leading-relaxed ${dark ? "text-white/60" : "text-[#0B1F3A]/60"}`}>{sub}</p>}
    </motion.div>
  );
}

/* Page hero for inner marketing pages */
export function PageIntro({ overline, title, sub }) {
  return (
    <section className="pt-36 pb-12 md:pt-44 md:pb-14 bg-white">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase mb-3" style={{ color: BLUE }}>{overline}</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08]">{title}</h1>
          {sub && <p className="mt-5 text-[#0B1F3A]/60 leading-relaxed">{sub}</p>}
        </motion.div>
      </div>
    </section>
  );
}

/* CTA band placed at the bottom of every content page */
export function CtaBand() {
  return (
    <section className="py-20" style={{ background: NAVY }}>
      <div className="mx-auto max-w-6xl px-5 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">See it live, in your therapeutic area</h2>
          <p className="text-white/55 mt-2">30 minutes with our team. No slides — the real platform.</p>
        </div>
        <Link to="/book-demo" data-testid="cta-band-demo-btn" className="inline-flex items-center gap-2 rounded-full bg-[#1F6FEB] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#1a5fd0] transition-colors shrink-0">
          Book a demo <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}

/* ================= Home sections ================= */
export function Hero() {
  return (
    <section className="relative overflow-hidden text-white" style={{ background: NAVY }}>
      <div className="absolute inset-0 opacity-[0.16] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800)" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(80% 60% at 50% 0%, rgba(31,111,235,0.28) 0%, rgba(11,31,58,0) 65%)" }} />
      <div className="relative mx-auto max-w-6xl px-5 md:px-8 pt-36 pb-24 md:pt-44 md:pb-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs text-white/70 mb-8">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: TEAL }} />
            The AI operating system for Medical Affairs
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl">
            Transform every scientific conversation into{" "}
            <span style={{ color: "#7CE8FF" }}>strategic intelligence</span>
          </h1>
          <p className="mt-7 text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            MSLs generate the signals in the field. Medical Affairs leadership receives synthesized
            intelligence and recommended strategic actions — every insight traced back to the
            conversations that produced it. No forms, no dashboards to chase.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book-demo" data-testid="hero-demo-btn" className="inline-flex items-center gap-2 rounded-full bg-[#1F6FEB] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#1a5fd0] transition-colors shadow-lg shadow-[#1F6FEB]/25">
              Book a demo <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
            <Link to="/how-it-works" data-testid="hero-how-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white/90 px-7 py-3.5 text-sm font-medium hover:bg-white/10 transition-colors">
              See how it works
            </Link>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }} className="mt-20 relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 md:p-8 overflow-hidden">
          <div className="absolute top-0 bottom-0 w-40 beam" style={{ background: "linear-gradient(90deg, transparent, rgba(0,229,255,0.08), transparent)" }} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative">
            {[
              ["60 sec", "to capture a meeting — by voice"],
              ["0 forms", "manual documentation eliminated"],
              ["4 questions", "every insight answers: what, why, how urgent, what next"],
              ["100%", "of intelligence traced to the field teams who generated it"],
            ].map(([stat, label]) => (
              <div key={stat}>
                <p className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ color: "#7CE8FF" }}>{stat}</p>
                <p className="text-xs md:text-sm text-white/55 mt-1.5 leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function Problem() {
  const pains = [
    { stat: "Hours weekly", text: "Industry surveys consistently report a large share of the MSL week going to CRM entries, reports and admin instead of science." },
    { stat: "Most", text: "field insight never reaches Medical Strategy, Publications, HEOR or Clinical Development in a usable, timely form." },
    { stat: "0", text: "systems today make the MSL's own next conversation better. They serve management first." },
  ];
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead
          overline="The problem"
          title="Field medicine runs on conversations. The tools run on forms."
          sub="Every CRM and KPI dashboard ever built for Medical Affairs was designed for reporting — not for the scientific exchange itself. The result: documentation burden up, intelligence lost, adoption forced."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {pains.map((p, i) => (
            <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="rounded-2xl border border-[#0B1F3A]/10 bg-[#F8FAFC] p-8">
              <p className="text-4xl font-semibold tracking-tight" style={{ color: BLUE }}>{p.stat}</p>
              <p className="mt-4 text-[#0B1F3A]/70 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Solution() {
  const points = [
    "An AI companion — never a surveillance tool",
    "Voice, calendar and live literature replace every form",
    "Value flows to the MSL first; intelligence emerges naturally",
    "Reporting happens automatically, invisibly, continuously",
  ];
  return (
    <section className="py-24 md:py-32" style={{ background: NAVY }}>
      <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHead
            dark
            overline="The solution"
            title="Not a CRM. Not a dashboard. A scientific companion."
            sub="MA Signals sits beside the MSL before, during and after every physician interaction — preparing them, listening for them, and remembering for them. The organization gets its intelligence as a by-product of MSLs simply doing great science."
          />
          <ul className="space-y-4">
            {points.map((pt) => (
              <li key={pt} className="flex items-start gap-3 text-white/80">
                <span className="mt-1 h-5 w-5 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(32,201,151,0.15)" }}>
                  <Check className="h-3 w-3" style={{ color: TEAL }} strokeWidth={2.5} />
                </span>
                {pt}
              </li>
            ))}
          </ul>
        </div>
        <motion.div {...fadeUp} className="relative">
          <img
            src="https://images.unsplash.com/photo-1758691737278-3af15b37af48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Medical professionals in scientific discussion"
            className="rounded-2xl border border-white/10 object-cover w-full aspect-[4/3]"
          />
          <div className="absolute -bottom-5 -left-5 rounded-xl bg-white shadow-xl p-4 flex items-center gap-3 node-float">
            <span className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "rgba(31,111,235,0.1)" }}>
              <Mic className="h-4 w-4" style={{ color: BLUE }} strokeWidth={1.5} />
            </span>
            <div className="text-[#0B1F3A]">
              <p className="text-xs font-semibold">Captured in 47 seconds</p>
              <p className="text-[11px] text-[#0B1F3A]/55">3 insights · 2 follow-ups · 1 AE auto-routed</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================= Buyer-readiness sections (Platform page) ================= */
export function WorkflowsSection() {
  const flows = [
    { role: "MSL", steps: ["Open Today — AI agenda & prep briefs ready", "Meet the KOL — fully present, no device", "Speak for 60 seconds — AI structures everything", "Review & confirm — follow-ups and insights filed"] },
    { role: "Medical Affairs Head", steps: ["Executive summary — under a minute", "What needs attention today — with recommendations", "Executive priorities — confidence, owners, rationale", "Drill to source — the MSLs and KOLs behind each signal"] },
    { role: "Compliance & PV", steps: ["Every conversation screened automatically", "AE and off-label signals flagged in real time", "Review queue with full context", "Complete audit trail, always on"] },
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="workflows-section">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead overline="Role-based workflows" title="Three roles. Three purpose-built experiences."
          sub="Each workspace is validated with its own users and workflows — in production these become separate applications with separate logins." />
        <div className="grid md:grid-cols-3 gap-6">
          {flows.map((f, i) => (
            <motion.div key={f.role} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-2xl border border-[#0B1F3A]/10 bg-white p-7" data-testid={`workflow-${i}`}>
              <p className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-4" style={{ color: BLUE }}>{f.role}</p>
              <ol className="space-y-3">
                {f.steps.map((s, j) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-[#0B1F3A]/70">
                    <span className="h-5 w-5 rounded-full bg-[#F8FAFC] border border-[#0B1F3A]/10 grid place-items-center text-[10px] font-semibold shrink-0 mt-0.5">{j + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IntegrationPilotSection() {
  const integrations = [
    ["Voice & transcription", "OpenAI Whisper speech-to-text", "Live"],
    ["AI reasoning", "Claude (Anthropic) via secured gateway", "Live"],
    ["Literature & trials", "PubMed and ClinicalTrials.gov APIs", "Live"],
    ["Email notifications", "Resend (demo requests, password resets)", "Live"],
    ["CRM sync (Veeva/OCE)", "Structured interaction export to your system of record", "Roadmap"],
    ["Calendar & SSO", "Google/Outlook calendar, SAML/OIDC single sign-on", "Roadmap"],
  ];
  const pilot = [
    ["Discovery", "45-minute session to agree scope: therapeutic area, team, success measures."],
    ["Sandbox pilot", "4–6 weeks with a small MSL group in an isolated environment — your data never trains shared models."],
    ["Validation", "Review against the agreed measures: capture time, insight volume, signal adoption, user feedback."],
    ["Security review & rollout", "InfoSec questionnaire, DPA, deployment model discussion, then phased rollout."],
  ];
  return (
    <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="integration-pilot-section">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead overline="Integration & procurement" title="Built to fit your stack — and your buying process."
          sub="A transparent view of what is live today, what is on the roadmap, and exactly how a pilot proceeds from first call to rollout." />
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div data-testid="integration-architecture">
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-4" style={{ color: BLUE }}>Integration architecture</p>
            <div className="rounded-2xl border border-[#0B1F3A]/10 bg-white overflow-hidden text-sm">
              {integrations.map(([area, detail, status], i) => (
                <div key={area} className={`flex items-center gap-4 px-5 py-3.5 ${i ? "border-t border-[#0B1F3A]/8" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#0B1F3A]">{area}</p>
                    <p className="text-xs text-[#0B1F3A]/50">{detail}</p>
                  </div>
                  <span className={`text-[10px] font-semibold tracking-wide uppercase rounded-full px-2.5 py-0.5 border shrink-0 ${status === "Live" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-[#0B1F3A]/50 bg-[#F8FAFC] border-[#0B1F3A]/15"}`}>{status}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#0B1F3A]/45 mt-3">
              Security details — encryption, access control, audit trail and data handling — are documented on our <Link to="/security" className="underline hover:text-[#0B1F3A]">Security page</Link>.
            </p>
          </div>
          <div data-testid="pilot-path">
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-4" style={{ color: BLUE }}>The pilot &amp; procurement path</p>
            <div className="space-y-3">
              {pilot.map(([t, d], i) => (
                <div key={t} className="flex gap-4 rounded-2xl border border-[#0B1F3A]/10 bg-white p-5">
                  <span className="h-8 w-8 rounded-full grid place-items-center text-xs font-semibold shrink-0" style={{ background: "rgba(31,111,235,0.08)", color: BLUE }}>{i + 1}</span>
                  <div>
                    <p className="font-semibold text-[#0B1F3A]">{t}</p>
                    <p className="text-sm text-[#0B1F3A]/60 mt-0.5 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#0B1F3A]/45 mt-3">
              Pilots are measured against success criteria agreed with you up front — we publish outcomes to you, not marketing claims.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TwoLayers() {
  const layers = [
    {
      tag: "Operational execution layer",
      name: "The MSL workspace",
      text: "A scientific companion that prepares, listens and remembers — so MSLs do great science, not admin.",
      points: ["2-minute AI prep brief before every meeting", "60-second voice capture, zero forms", "Follow-ups, territory memory and time-back tracking"],
      icon: Mic,
    },
    {
      tag: "Strategic intelligence layer",
      name: "The Medical Affairs workspace",
      text: "An AI Chief of Staff for Medical Affairs Heads — not another CRM, KPI tracker or BI tool.",
      points: ["Executive summary readable in under a minute", "Executive priorities with confidence and suggested owners", "Every signal attributed to the MSLs and KOLs behind it"],
      icon: Radar,
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="two-layers-section">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead
          overline="One platform, two products"
          title="The field generates the signals. Leadership gets the intelligence."
          sub="Two complementary applications with different users and different workflows — connected by one continuously learning intelligence engine. Compliance and PV watch over every conversation in both."
        />
        <div className="grid md:grid-cols-2 gap-6">
          {layers.map((l, i) => (
            <motion.div key={l.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              className="rounded-2xl border border-[#0B1F3A]/10 bg-white p-8 hover:border-[#1F6FEB]/40 hover:shadow-md transition-all"
              data-testid={`layer-${i}`}>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "rgba(31,111,235,0.08)" }}>
                  <l.icon className="h-5 w-5" style={{ color: BLUE }} strokeWidth={1.5} />
                </span>
                <span className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BLUE }}>{l.tag}</span>
              </div>
              <p className="font-semibold text-xl tracking-tight mb-2">{l.name}</p>
              <p className="text-sm text-[#0B1F3A]/60 leading-relaxed mb-5">{l.text}</p>
              <ul className="space-y-2.5">
                {l.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-[#0B1F3A]/75">
                    <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: TEAL }} strokeWidth={2} /> {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp} className="mt-8 rounded-2xl border border-[#0B1F3A]/10 bg-white p-7 md:p-8" data-testid="attribution-highlight">
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-10">
            <div className="md:max-w-sm shrink-0">
              <p className="font-semibold text-lg tracking-tight mb-1.5">Signal attribution, built in</p>
              <p className="text-sm text-[#0B1F3A]/60 leading-relaxed">Leadership never asks &ldquo;who told us this?&rdquo; — every signal shows the MSLs, KOLs, regions and conversations behind it.</p>
            </div>
            <div className="flex-1 rounded-xl bg-[#F8FAFC] border border-[#0B1F3A]/8 p-5 text-sm">
              <p className="font-medium text-[#0B1F3A]">Indian subgroup evidence becoming a major adoption lever</p>
              <p className="text-xs text-[#0B1F3A]/55 mt-2">Observed by <span className="font-medium text-[#0B1F3A]/80">Priya Sharma (West) · Sneha Nair (South) · Rahul Gupta (North)</span></p>
              <p className="text-xs text-[#0B1F3A]/55 mt-1">3 MSLs · 6 interactions · 5 KOLs · Confidence: High</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================= How it works page sections ================= */
export function JourneySection() {
  const steps = [
    { icon: CalendarDays, name: "Prepare", text: "A 2-minute AI brief before every meeting: profile, history, fresh publications, trials, competitor moves, suggested questions." },
    { icon: Users, name: "Meet", text: "Walk in sharper. Everything you need to have a better scientific conversation, nothing you don't." },
    { icon: Mic, name: "Capture", text: "Just talk for 60 seconds. AI identifies the physician, structures the note, flags AEs — zero data entry." },
    { icon: ListChecks, name: "Follow up", text: "Promises become tracked follow-ups with AI-drafted emails. Nothing slips." },
    { icon: Search, name: "Learn", text: "Ask your territory memory anything, in natural language or by voice. Instant recall before the next visit." },
    { icon: Radar, name: "Intelligence", text: "Every conversation silently feeds executive priorities, evidence gaps and strategy — attributed back to you and your KOLs." },
  ];
  return (
    <section className="py-20 md:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead
          overline="The MSL journey"
          title="Six moments. One companion."
          sub="Prepare → Meet → Capture → Follow up → Learn → Intelligence. Built around the way field medicine actually happens — not around reports."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }} className="group rounded-2xl border border-[#0B1F3A]/10 bg-white p-8 hover:border-[#1F6FEB]/40 hover:shadow-md transition-all" data-testid={`journey-step-${s.name.toLowerCase().replace(" ", "-")}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "rgba(31,111,235,0.08)" }}>
                  <s.icon className="h-5 w-5" style={{ color: BLUE }} strokeWidth={1.5} />
                </span>
                <span className="text-xs font-mono text-[#0B1F3A]/40">0{i + 1}</span>
              </div>
              <p className="font-semibold text-lg tracking-tight mb-2">{s.name}</p>
              <p className="text-sm text-[#0B1F3A]/60 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BenefitsSection() {
  const rows = [
    ["MSLs", "Hours back every week, sharper conversations, a memory that never forgets a promise."],
    ["Medical Affairs Leadership", "An AI Chief of Staff: executive priorities with confidence, suggested owners and the exact field experts to call — every insight attributed to its source."],
    ["Medical Information", "Inquiries and material requests captured at the source, automatically."],
    ["Medical Strategy & Publications", "Evidence gaps and publication opportunities surface themselves."],
    ["RWE / HEOR / Clin Dev", "Investigator signals, data needs and IIT opportunities from real conversations."],
    ["Compliance & PV", "AEs and off-label mentions detected in speech and routed instantly, with full audit trail."],
  ];
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead overline="Who benefits" title="Value for the individual first. Intelligence for everyone after." />
        <div className="rounded-2xl border border-[#0B1F3A]/10 bg-white divide-y divide-[#0B1F3A]/8 overflow-hidden">
          {rows.map(([who, what], i) => (
            <motion.div key={who} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.04 }} className="grid md:grid-cols-[240px,1fr] gap-2 md:gap-8 px-7 py-6">
              <p className="font-semibold tracking-tight">{who}</p>
              <p className="text-sm text-[#0B1F3A]/60 leading-relaxed">{what}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================= Platform page sections ================= */
export function ModulesSection() {
  const featured = {
    icon: BookOpen,
    name: "Meeting Brief",
    text: "Doctor profile, recent interactions, scientific interests, live publications, competitor updates, objectives and suggested questions — compiled automatically before you even ask for it.",
    points: ["Live PubMed & ClinicalTrials.gov feeds", "Relationship history & sentiment", "Suggested scientific questions"],
  };
  const mods = [
    { icon: Mic, name: "AI Meeting Capture", text: "Voice-first, one minute. Extracts topics, objections, medical inquiries, evidence gaps, competitor mentions and action items." },
    { icon: Brain, name: "Personal AI Assistant", text: "A chief of staff that remembers promises, chases follow-ups, surfaces new publications and preps your day." },
    { icon: Network, name: "Scientific Intelligence", text: "Conversations aggregate into evidence gaps, competitor narratives, emerging trends, publication and RWE opportunities." },
    { icon: Gauge, name: "Medical Affairs Command Center", text: "An AI Chief of Staff: executive summary, what needs attention today, executive priorities with confidence, owners and named field experts to call." },
  ];
  return (
    <section className="pb-20 md:pb-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        {/* Featured module — 01 */}
        <motion.div {...fadeUp} className="rounded-2xl border border-[#0B1F3A]/10 overflow-hidden mb-6" data-testid="module-meeting-brief" style={{ background: NAVY }}>
          <div className="grid md:grid-cols-2">
            <div className="p-8 md:p-10 text-white">
              <div className="flex items-center justify-between mb-5">
                <span className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "rgba(0,229,255,0.12)" }}>
                  <featured.icon className="h-5 w-5" style={{ color: "#7CE8FF" }} strokeWidth={1.5} />
                </span>
                <span className="text-xs font-mono text-white/40">01 / 05</span>
              </div>
              <p className="font-semibold text-xl tracking-tight mb-2">{featured.name}</p>
              <p className="text-sm text-white/60 leading-relaxed mb-6">{featured.text}</p>
              <ul className="space-y-2.5">
                {featured.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-white/80">
                    <Check className="h-4 w-4 shrink-0" style={{ color: TEAL }} strokeWidth={2} /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative hidden md:block">
              <img src="https://images.unsplash.com/photo-1785740057479-7d5d8227f089?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Signal intelligence" className="absolute inset-0 h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0B1F3A 0%, rgba(11,31,58,0.2) 60%)" }} />
            </div>
          </div>
        </motion.div>

        {/* Modules 02–05 — equal grid, nothing hanging */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {mods.map((m, i) => (
            <motion.div key={m.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }} className="rounded-2xl border border-[#0B1F3A]/10 bg-[#F8FAFC] p-7 hover:border-[#1F6FEB]/40 hover:shadow-md transition-all" data-testid={`module-${m.name.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between mb-5">
                <span className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "rgba(32,201,151,0.12)" }}>
                  <m.icon className="h-5 w-5" style={{ color: "#0f9d74" }} strokeWidth={1.5} />
                </span>
                <span className="text-xs font-mono text-[#0B1F3A]/35">0{i + 2} / 05</span>
              </div>
              <p className="font-semibold tracking-tight mb-2">{m.name}</p>
              <p className="text-sm text-[#0B1F3A]/60 leading-relaxed">{m.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AiIntelligenceSection() {
  const feats = [
    [Mic, "Voice transcription"], [Sparkles, "Generative summaries"], [Waypoints, "Knowledge graph"],
    [Search, "Natural-language querying"], [Mail, "Auto-drafted follow-up emails"], [BookOpen, "Suggested publications"],
    [Radar, "Evidence recommendation engine"], [Brain, "Next best action"], [FileSearch, "AI meeting preparation"],
  ];
  return (
    <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0 opacity-[0.12] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800)" }} />
      <div className="relative mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead
          dark
          overline="AI intelligence"
          title="AI-native from the first tap"
          sub="Not AI sprinkled on top of a CRM — an intelligence engine with a product around it."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {feats.map(([Icon, label], i) => (
            <motion.div key={label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.04 }} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-5 py-4">
              <Icon className="h-5 w-5 shrink-0" style={{ color: "#7CE8FF" }} strokeWidth={1.5} />
              <span className="text-sm text-white/85">{label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================= Security page sections ================= */
export function SecuritySection() {
  const items = [
    [Lock, "Approved-user access", "Role-based login for MSL, Leadership and Compliance. Access is provisioned by your administrator — no open registration."],
    [ShieldCheck, "Compliance by design", "AE and off-label detection with immutable audit trails — PV workflows built in, not bolted on."],
    [FileSearch, "Data stewardship", "Voice audio transcribed then discarded. Nothing saved until the MSL confirms. Clear AI-processing indicators."],
  ];
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHead
            overline="Enterprise security"
            title="Trusted enough for pharma. Loved enough for daily use."
            sub="Built for validated environments and global Medical Affairs organizations."
          />
          <div className="space-y-6">
            {items.map(([Icon, t, d]) => (
              <div key={t} className="flex gap-4">
                <span className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "rgba(31,111,235,0.08)" }}>
                  <Icon className="h-5 w-5" style={{ color: BLUE }} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-semibold tracking-tight">{t}</p>
                  <p className="text-sm text-[#0B1F3A]/60 leading-relaxed mt-1">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <motion.img
          {...fadeUp}
          src="https://images.unsplash.com/photo-1614064548237-096f735f344f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Enterprise security"
          className="rounded-2xl border border-[#0B1F3A]/10 object-cover w-full aspect-[4/3]"
        />
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  const quotes = [
    ["\u201CI stopped writing reports three months ago. Nobody noticed — because the intelligence got better.\u201D", "Senior MSL, Diabetes & Obesity", "Top-10 pharma (pilot placeholder)"],
    ["\u201CFor the first time I see what the field is actually hearing — the week it happens, not the quarter after.\u201D", "VP Medical Affairs", "Global biopharma (pilot placeholder)"],
    ["\u201CAE capture went from a form nobody loved to something that just… happens. Audit-ready, every time.\u201D", "Head of Pharmacovigilance", "Mid-size biotech (pilot placeholder)"],
  ];
  return (
    <section className="py-20 md:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHead overline="Early signals" title="What pilots are telling us" />
        <div className="grid md:grid-cols-3 gap-6">
          {quotes.map(([q, who, org], i) => (
            <motion.figure key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="rounded-2xl border border-[#0B1F3A]/10 bg-white p-8">
              <Quote className="h-5 w-5 mb-4" style={{ color: TEAL }} strokeWidth={1.5} />
              <blockquote className="text-[#0B1F3A]/80 leading-relaxed">{q}</blockquote>
              <figcaption className="mt-6">
                <p className="text-sm font-semibold">{who}</p>
                <p className="text-xs text-[#0B1F3A]/50 mt-0.5">{org}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================= Demo form ================= */
export function DemoFormSection() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", therapeutic_area: "", country: "",
    team_size: "", current_crm: "", use_case: "", procurement_timing: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "rounded-xl bg-white/[0.06] border border-white/15 px-5 py-3.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-[#1F6FEB] transition-colors";
  const selectCls = `${inputCls} appearance-none [&>option]:text-[#0B1F3A]`;

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.includes("@")) return toast.error("Please add your name and a valid email");
    setBusy(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      track("demo_request", "/book-demo");
      setSent(true);
    } catch {
      toast.error("Could not send — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pt-36 pb-24 md:pt-44 md:pb-32 relative overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 100%, rgba(31,111,235,0.25) 0%, rgba(11,31,58,0) 70%)" }} />
      <div className="relative mx-auto max-w-3xl px-5 md:px-8 text-center">
        <SignalMark size={52} />
        <h1 className="mt-8 text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
          See your field intelligence come alive
        </h1>
        <p className="mt-4 text-white/60 max-w-xl mx-auto leading-relaxed">
          30 minutes. Your therapeutic area. A live look at what your team&rsquo;s conversations could be telling you.
        </p>
        {sent ? (
          <div className="mt-12 rounded-2xl border border-white/15 bg-white/[0.05] p-10 text-left" data-testid="demo-success">
            <span className="h-12 w-12 rounded-full grid place-items-center mx-auto mb-4" style={{ background: "rgba(32,201,151,0.15)" }}>
              <Check className="h-6 w-6" style={{ color: TEAL }} strokeWidth={2} />
            </span>
            <p className="text-white font-semibold text-lg text-center">Request received — thank you.</p>
            <div className="mt-5 max-w-md mx-auto space-y-2 text-sm text-white/60">
              <p>1. We&rsquo;ll confirm by email within one business day.</p>
              <p>2. A 30-minute walkthrough tailored to your therapeutic area and priority use case.</p>
              <p>3. If it fits, we&rsquo;ll propose a scoped pilot with agreed success measures.</p>
            </div>
            <p className="mt-6 text-xs text-white/40 text-center">
              Your details are used only to arrange this demo. We don&rsquo;t share them with third parties, and you can ask us to delete them at any time.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 text-left grid sm:grid-cols-2 gap-4" data-testid="demo-form">
            <input data-testid="demo-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
            <input data-testid="demo-email" type="email" value={form.email} onChange={set("email")} placeholder="Work email *" className={inputCls} />
            <input data-testid="demo-company" value={form.company} onChange={set("company")} placeholder="Company" className={inputCls} />
            <input data-testid="demo-role" value={form.role} onChange={set("role")} placeholder="Role (e.g. Medical Director)" className={inputCls} />
            <input data-testid="demo-ta" value={form.therapeutic_area} onChange={set("therapeutic_area")} placeholder="Therapeutic area(s)" className={inputCls} />
            <input data-testid="demo-country" value={form.country} onChange={set("country")} placeholder="Country / region" className={inputCls} />
            <select data-testid="demo-team-size" value={form.team_size} onChange={set("team_size")} className={selectCls}>
              <option value="">Field team size…</option>
              {["1–5 MSLs", "6–15 MSLs", "16–50 MSLs", "50+ MSLs"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <input data-testid="demo-crm" value={form.current_crm} onChange={set("current_crm")} placeholder="Current CRM (e.g. Veeva)" className={inputCls} />
            <select data-testid="demo-use-case" value={form.use_case} onChange={set("use_case")} className={selectCls}>
              <option value="">Priority use case…</option>
              {["Insight capture & synthesis", "Executive field intelligence", "KOL engagement quality", "Compliance & PV monitoring", "MSL productivity / time back", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select data-testid="demo-timing" value={form.procurement_timing} onChange={set("procurement_timing")} className={selectCls}>
              <option value="">Procurement timing…</option>
              {["Exploring — no timeline yet", "Pilot within 3 months", "Budget cycle this year", "Urgent — active evaluation"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <textarea data-testid="demo-message" value={form.message} onChange={set("message")} placeholder="Anything specific you'd like to see?" rows={3} className={`sm:col-span-2 ${inputCls} resize-none`} />
            <button data-testid="demo-submit" type="submit" disabled={busy} className="sm:col-span-2 rounded-full bg-[#1F6FEB] text-white py-4 text-sm font-semibold hover:bg-[#1a5fd0] transition-colors disabled:opacity-60">
              {busy ? "Sending…" : "Book a demo"}
            </button>
            <p className="sm:col-span-2 text-xs text-white/40 text-center" data-testid="demo-privacy">
              By submitting, you agree we may contact you about this request. Your details are used only to arrange the demo, never shared with third parties, and deleted on request.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
